import React, { useEffect, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { validateToken } from "./intakeApi";
import PatientReportHeader from "../../frame/PatientReportHeader";
import ReportFooter from "../../frame/GetReportFooter";

// ─── Font injection ────────────────────────────────────────────────────────────
const injectFont = () => {
	if (document.getElementById("validate-step-font")) return;
	const link = document.createElement("link");
	link.id = "validate-step-font";
	link.rel = "stylesheet";
	link.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap";
	document.head.appendChild(link);
};

// ─── Inline styles — matches GetPatientReportForm / UploadPatientReportForm ────
const injectStyles = () => {
	if (document.getElementById("validate-step-styles")) return;
	const style = document.createElement("style");
	style.id = "validate-step-styles";
	style.textContent = `
		.vs-wrap, .vs-wrap *, .vs-wrap *::before, .vs-wrap *::after {
			box-sizing: border-box;
		}
		.vs-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			width: 100%;
			max-width: 780px;
			margin: 0 auto;
		}

		/* ── Split card — connects flush to header above ── */
		.vs-card {
			width: 100%;
			display: grid;
			grid-template-columns: 200px 1fr;
			background: #fff;
			border-radius: 0 0 20px 20px;
			border: 1px solid rgba(0,0,0,0.06);
			border-top: none;
			box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 20px 48px -20px rgba(0,0,0,0.14);
			overflow: hidden;
		}

		/* ── Left orange panel ── */
		.vs-left {
			background: linear-gradient(155deg, #F4A96A 0%, #E47029 50%, #B94D1A 100%);
			padding: 32px 22px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			position: relative;
			overflow: hidden;
		}
		.vs-left::before {
			content: '';
			position: absolute;
			width: 200px; height: 200px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.14);
			right: -80px; top: -80px;
		}
		.vs-left::after {
			content: '';
			position: absolute;
			width: 140px; height: 140px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.12);
			left: -50px; bottom: -50px;
		}
		.vs-left__top { position: relative; z-index: 1; }
		.vs-left__icon {
			width: 44px; height: 44px;
			border-radius: 12px;
			background: rgba(255,255,255,0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 14px;
		}
		.vs-left__title {
			font-family: 'Manrope', sans-serif;
			font-size: 16px;
			font-weight: 800;
			color: #fff;
			letter-spacing: -0.01em;
			line-height: 1.3;
			margin-bottom: 8px;
		}
		.vs-left__sub {
			font-size: 12px;
			color: rgba(255,255,255,0.78);
			line-height: 1.55;
		}
		.vs-left__bottom { position: relative; z-index: 1; }
		.vs-left__note {
			display: flex;
			align-items: center;
			gap: 7px;
			font-size: 11px;
			color: rgba(255,255,255,0.7);
		}

		/* ── Right panel ── */
		.vs-right { padding: 28px 24px; min-width: 0; }
		.vs-right__label {
			font-size: 11px;
			font-weight: 700;
			color: #E47029;
			text-transform: uppercase;
			letter-spacing: 0.07em;
			margin-bottom: 6px;
		}
		.vs-right__title {
			font-family: 'Manrope', sans-serif;
			font-size: 18px;
			font-weight: 800;
			color: #18181A;
			letter-spacing: -0.01em;
			margin-bottom: 5px;
		}
		.vs-right__sub {
			font-size: 12.5px;
			color: #6B6760;
			line-height: 1.55;
			margin-bottom: 20px;
		}

		/* ── Error box ── */
		.vs-error {
			background: #FCEBEB;
			border: 1px solid #F2C6C6;
			border-radius: 10px;
			padding: 11px 13px;
			margin-bottom: 18px;
		}
		.vs-error__text {
			font-size: 12.5px;
			color: #A32D2D;
			line-height: 1.55;
		}

		/* fields */
		.vs-fields {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 14px;
			margin-bottom: 18px;
		}
		.vs-field label {
			display: block;
			font-size: 11.5px;
			font-weight: 600;
			color: #5B5B60;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			margin-bottom: 7px;
		}
		.vs-field input {
			width: 100%;
			height: 46px;
			padding: 0 14px;
			border: 1.5px solid rgba(0,0,0,0.11);
			border-radius: 10px;
			font-size: 14px;
			font-family: inherit;
			color: #18181A;
			background: #FAFAFA;
			-webkit-appearance: none;
			appearance: none;
			transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
		}
		.vs-field input:hover { border-color: #c2bdb3; }
		.vs-field input:focus {
			outline: none;
			border-color: #E47029;
			box-shadow: 0 0 0 3px rgba(228,112,41,0.12);
			background: #fff;
		}
		.vs-field input:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
		.vs-field__hint {
			font-size: 11px;
			color: #C0BDB8;
			margin-top: 4px;
		}

		/* button */
		.vs-btn {
			width: 100%;
			height: 50px;
			border: none;
			border-radius: 12px;
			background: radial-gradient(73.95% 73.95% at 14.58% 11.46%, #EE8F37 0%, #E47029 100%);
			color: #fff;
			font-family: 'Manrope', sans-serif;
			font-size: 15px;
			font-weight: 700;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
			transition: opacity 0.15s, transform 0.1s;
			box-shadow: 0 8px 22px -6px rgba(228,112,41,0.5);
			letter-spacing: 0.01em;
		}
		.vs-btn:hover:not(:disabled) { opacity: 0.92; }
		.vs-btn:active:not(:disabled) { transform: scale(0.99); }
		.vs-btn:disabled {
			background: #E5C9B0;
			box-shadow: none;
			cursor: not-allowed;
			opacity: 1;
		}
		.vs-security {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 5px;
			margin-top: 10px;
			font-size: 11px;
			color: #C0BDB8;
		}

		/* ── Responsive ── */
		@media (max-width: 680px) {
			.vs-card {
				grid-template-columns: 1fr;
				border-radius: 0 0 16px 16px;
			}
			.vs-left { padding: 20px; min-height: auto; }
			.vs-right { padding: 20px; }
			.vs-fields { grid-template-columns: 1fr; }
		}
	`;
	document.head.appendChild(style);
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const ShieldIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="22" height="22" aria-hidden="true">
		<path d="M12 2 4 6v6c0 5.5 3.8 10.5 8 12 4.2-1.5 8-6.5 8-12V6z" />
		<path d="M9 12l2 2 4-4" />
	</svg>
);

const LockIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11" aria-hidden="true">
		<rect x="4" y="10" width="16" height="10" rx="2" />
		<path d="M8 10V7a4 4 0 0 1 8 0v3" />
	</svg>
);

const ArrowIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15" aria-hidden="true">
		<line x1="5" y1="12" x2="19" y2="12" />
		<polyline points="12,5 19,12 12,19" />
	</svg>
);

// ─── Main component ────────────────────────────────────────────────────────────
export default function ValidateStep({ token, orgId, logo, orgInfo, onValidated, onExpired }) {
	const [hin, setHin] = useState("");
	const [dob, setDob] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		injectFont();
		injectStyles();
	}, []);

	const canSubmit = hin.trim() && dob && !submitting;

	const handleValidate = async () => {
		setSubmitting(true);
		setError(null);
		try {
			const result = await validateToken(hin.trim(), dob, token, orgId);

			if (result.status === 200 && result.data?.valid) {
				onValidated({ hin: hin.trim(), dob, token, orgId });
				return;
			}

			const upstreamData = result.data || result.error?.response?.data || {};
			const msg = upstreamData.message || "";

			const isDeadLink =
				result.status === 410 ||
				upstreamData.expired === true ||
				(typeof msg === "string" && /expired|already been used|has been used|no longer valid/i.test(msg));

			if (isDeadLink) {
				onExpired("This form has already been submitted or the link is no longer valid. Please contact your clinic if you need to make changes.");
				return;
			}

			if (!result.status || result.status >= 500) {
				setError("We couldn't reach the validation service. Please try again.");
			} else {
				setError(msg || "The HIN or date of birth doesn't match our records. Please try again.");
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="vs-wrap">

			{/* ── Header — same component used across patient-facing pages ── */}
			<PatientReportHeader
				logo={logo}
				address={orgInfo?.address}
				contactNum={orgInfo?.contactNum}
			/>

			{/* ── Split card — connects flush to header ── */}
			<div className="vs-card">

				{/* Left orange panel */}
				<div className="vs-left">
					<div className="vs-left__top">
						<div className="vs-left__icon">
							<ShieldIcon />
						</div>
						<div className="vs-left__title">Identity Check</div>
						<div className="vs-left__sub">
							We verify your information before opening the intake form. Your data is kept secure and private.
						</div>
					</div>
					<div className="vs-left__bottom">
						<div className="vs-left__note">
							<LockIcon />
							Secure &amp; private
						</div>
					</div>
				</div>

				{/* Right form panel */}
				<div className="vs-right">
					<div className="vs-right__label">Verify identity</div>
					<div className="vs-right__title">Patient Intake Form</div>
					<div className="vs-right__sub">
						Enter your health card number and date of birth to verify your identity.
					</div>

					{error && (
						<div className="vs-error">
							<div className="vs-error__text">{error}</div>
						</div>
					)}

					<div className="vs-fields">
						<div className="vs-field">
							<label htmlFor="hin">HIN</label>
							<input
								id="hin"
								type="text"
								placeholder="1234567890"
								value={hin}
								disabled={submitting}
								onChange={(e) => setHin(e.target.value)}
								autoComplete="off"
							/>
							<div className="vs-field__hint">10-digit number</div>
						</div>
						<div className="vs-field">
							<label htmlFor="dob">Date of Birth</label>
							<input
								id="dob"
								type="date"
								value={dob}
								disabled={submitting}
								onChange={(e) => setDob(e.target.value)}
							/>
							<div className="vs-field__hint">e.g. 1990-01-15</div>
						</div>
					</div>

					<button
						className="vs-btn"
						disabled={!canSubmit}
						onClick={handleValidate}
					>
						{submitting ? (
							<CircularProgress size={18} style={{ color: "#fff" }} />
						) : (
							<ArrowIcon />
						)}
						{submitting ? "Validating…" : "Validate"}
					</button>

					<div className="vs-security">
						<LockIcon />
						256-bit encrypted · PHIPA compliant
					</div>
				</div>

			</div>

			{/* ── Footer ── */}
			<ReportFooter />
		</div>
	);
}